import React, { useState, useRef } from "react";
import {
  TextField,
  Menu,
  Box,
  Modal,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import dayjs from "dayjs";

interface CalendarWrapProps {
  onDateChange?: (dates: string[]) => void;
}

const formatDate = (date: Date | null): string => {
  if (!date) return "";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};
const getDaysDifference = (start: Date, end: Date): number => {
  const oneDay = 24 * 60 * 60 * 1000; // миллисекунды в одном дне
  const startDate = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate()
  );
  const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round(
    Math.abs((endDate.getTime() - startDate.getTime()) / oneDay)
  );
};

function CalendarWrap({ onDateChange }: CalendarWrapProps) {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startInputValue, setStartInputValue] =
    useState<string>("Выберите дату");
  const [endInputValue, setEndInputValue] = useState<string>("Выберите дату");
  const [responseValue, setResponseValue] = useState<string[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isSelectingStart, setIsSelectingStart] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const calendarRef = useRef(null);
  const currentDate = new Date();

  const handleOpenStart = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
    setIsSelectingStart(true);
  };

  const handleOpenEnd = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
    setIsSelectingStart(false);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => setAnchorEl(null), 300);
  };

  const handleCalendarChange = (date: any) => {
    if (isSelectingStart) {
      setStartDate(date);
      setStartInputValue(formatDate(date));
      setIsSelectingStart(false);
    } else {
      let finalStartDate = startDate;
      let finalEndDate = date;

      if (startDate && date < startDate) {
        finalStartDate = date;
        finalEndDate = startDate;
      }

      if (finalStartDate && finalEndDate) {
        const daysDifference = getDaysDifference(finalStartDate, finalEndDate);

        if (daysDifference > 28) {
          //   alert('Выберите период не более 28 дней');
          setModalOpen(true);
          return;
        }
      }
      setStartDate(finalStartDate);
      setStartInputValue(formatDate(finalStartDate));
      setEndDate(finalEndDate);
      setEndInputValue(formatDate(finalEndDate));
      const start = dayjs(finalStartDate).format("DD-MM-YYYY");
      const end = dayjs(finalEndDate).format("DD-MM-YYYY");
      const newResponseValue = [start, end];
      setResponseValue(newResponseValue);

      if (onDateChange) {
        onDateChange(newResponseValue);
      }

      handleClose();
      setIsSelectingStart(true);
    }
  };

  // Функция для отключения будущих дат
  const tileDisabled = ({ date, view }: { date: Date; view: string }) => {
    // Отключаем только в режиме просмотра месяцев
    if (view === "month") {
      // Отключаем все даты после текущей
      return date > currentDate;
    }
    return false;
  };

  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view === "month") {
      const classes = [];

      // Подсвечиваем диапазон
      if (startDate && endDate) {
        const tileTime = date.getTime();
        const startTime = startDate.getTime();
        const endTime = endDate.getTime();

        if (tileTime >= startTime && tileTime <= endTime) {
          classes.push("highlight-range");
        }
      }

      // Добавляем класс для будущих дат
      if (date > currentDate) {
        classes.push("future-date-disabled");
      }

      return classes.length > 0 ? classes.join(" ") : null;
    }
    return null;
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
  };
  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <div>
      <div style={{ display: "flex" }}>
        <TextField
          id="start-date"
          label="Начало периода"
          value={startInputValue}
          size="small"
          color="secondary"
          focused
          sx={{ marginRight: "1vw" }}
          onClick={handleOpenStart}
          inputProps={{ readOnly: true }}
        />
        <TextField
          id="end-date"
          label="Конец периода"
          value={endInputValue}
          size="small"
          color="secondary"
          focused
          sx={{ marginRight: "1vw", width: 250 }}
          onClick={handleOpenEnd}
          inputProps={{ readOnly: true }}
        />
      </div>
      <Menu
        id="calendar-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "calendar-button",
        }}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "var(--bcg)",
            borderRadius: "8px",
          },
        }}
      >
        <Box
          ref={calendarRef}
          sx={{
            padding: "10px",
            "& .react-calendar": {
              background: "var(--pink)",
              border: "2px solid rgba(53, 56, 89, 1)",
              color: "black",
              borderRadius: "8px",
              padding: "10px",
              width: "300px",
            },
            "& .react-calendar abbr": {
              width: "20px",
              textDecoration: "none",
              color: "black",
            },
            "& .react-calendar button": {
              color: "rgb(128, 191, 255)",
              borderRadius: "4px",
              margin: "2px",
            },
            "& .react-calendar button:hover": {
              background: "rgba(47, 29, 31, 0.3)",
              color: "white",
            },
            "& .react-calendar__tile--now": {
              background: "rgba(240, 241, 243, 0.3)",
            },
            "& .highlight-range": {
              backgroundColor: "var(--bcg)",
              color: "white",
              borderRadius: "4px",
            },
            // Стили для будущих дат
            "& .future-date-disabled": {
              backgroundColor: "#8e8989ff",
              color: "#ccc",
            },
            // Стили для отключенных кнопок (будущих дат)
            "& .react-calendar__tile:disabled": {
              backgroundColor: "#8e8989ff",
              color: "#d0d0d0",
            },
            "& .react-calendar__tile:disabled:hover": {
              backgroundColor: "#8e8989ff",
              color: "#d0d0d0",
              cursor: "not-allowed",
            },
            "& .react-calendar__navigation button": {
              color: "white",
              background: "rgba(52, 48, 48, 0.1)",
            },
            "& .react-calendar__navigation button:hover": {
              background: "rgba(229, 217, 208, 0.5)",
              color: "black",
            },
          }}
        >
          <div
            style={{
              marginBottom: "10px",
              fontWeight: "bold",
              color: "black",
              backgroundColor: "var(--bcg)",
            }}
          >
            {isSelectingStart
              ? "Выберите начальную дату"
              : "Выберите конечную дату"}
          </div>
          <Calendar
            selectRange={false}
            onChange={handleCalendarChange}
            value={isSelectingStart ? startDate : endDate}
            locale="ru-RU"
            tileClassName={tileClassName}
            tileDisabled={tileDisabled} // Отключаем будущие даты
            maxDate={currentDate} // Устанавливаем максимальную дату
          />
          <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
            <Box sx={modalStyle}>
              <IconButton
                aria-label="close"
                onClick={handleCloseModal}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>

              <Typography variant="h6" component="h2">
                Выберите период не превышающий 28 дней
              </Typography>
            </Box>
          </Modal>
        </Box>
      </Menu>
    </div>
  );
}

export default CalendarWrap;
